import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaPaperPlane, FaQuoteLeft } from "react-icons/fa";
import Seo from "../components/Seo";

const initialFeedback = {
  name: "",
  email: "",
  school: "",
  role: "",
  message: "",
  permission: false,
};

const FeedbackForm = () => {
  const [feedback, setFeedback] = useState(initialFeedback);

  const update = (field, value) => {
    setFeedback((current) => ({ ...current, [field]: value }));
  };

  const submit = (event) => {
    event.preventDefault();
    const body = [
      `Name: ${feedback.name}`,
      `Email: ${feedback.email}`,
      `School: ${feedback.school}`,
      `Role: ${feedback.role || "Not provided"}`,
      `Permission to feature: ${feedback.permission ? "Yes" : "No"}`,
      "",
      "Feedback:",
      feedback.message,
    ].join("\n");

    window.location.assign(`mailto:support@classnx.com?subject=${encodeURIComponent("ClassNX feedback")}&body=${encodeURIComponent(body)}`);
  };

  const inputClass = "mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-normal outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100";

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 px-4 py-8 text-slate-900 sm:px-6 sm:py-12">
      <Seo title="Share Feedback | ClassNX" description="Share your experience and help improve ClassNX." canonical="/feedback" robots="noindex, follow" />
      <div className="mx-auto max-w-6xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-blue-700 transition hover:text-blue-900"><FaArrowLeft className="text-xs" /> Back to home</Link>

        <div className="mt-7 grid overflow-hidden rounded-[2rem] border border-blue-100 bg-white shadow-[0_30px_90px_-40px_rgba(30,64,175,0.45)] lg:grid-cols-[0.78fr_1.22fr]">
          <section className="relative isolate overflow-hidden bg-gradient-to-br from-blue-700 via-indigo-700 to-slate-950 p-8 text-white sm:p-10 lg:p-12">
            <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-fuchsia-400/15 blur-3xl" />
            <div className="relative flex h-full min-h-72 flex-col justify-between">
              <div>
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-2xl text-cyan-200 backdrop-blur"><FaQuoteLeft /></div>
                <p className="mt-10 text-xs font-bold uppercase tracking-[0.26em] text-cyan-200">Your voice matters</p>
                <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight">Help shape the next ClassNX experience.</h1>
                <p className="mt-5 max-w-md text-base leading-7 text-blue-100/75">Tell us what works well, what gets in your way, and what your school needs next.</p>
              </div>
              <p className="mt-10 border-t border-white/15 pt-6 text-sm leading-6 text-blue-100/65">We only feature verified feedback when you explicitly give permission.</p>
            </div>
          </section>

          <section className="p-6 sm:p-10 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">Feedback form</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Share your experience</h2>
            <p className="mt-3 text-sm leading-6 text-slate-500">A few details help us understand the context behind your feedback.</p>

            <form onSubmit={submit} className="mt-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">Your name<input required type="text" value={feedback.name} onChange={(event) => update("name", event.target.value)} className={inputClass} placeholder="Full name" /></label>
                <label className="text-sm font-semibold text-slate-700">Email address<input required type="email" value={feedback.email} onChange={(event) => update("email", event.target.value)} className={inputClass} placeholder="you@school.edu" /></label>
                <label className="text-sm font-semibold text-slate-700">School name<input required type="text" value={feedback.school} onChange={(event) => update("school", event.target.value)} className={inputClass} placeholder="Your school" /></label>
                <label className="text-sm font-semibold text-slate-700">Your role<input type="text" value={feedback.role} onChange={(event) => update("role", event.target.value)} className={inputClass} placeholder="Teacher, admin, parent..." /></label>
                <label className="text-sm font-semibold text-slate-700 sm:col-span-2">Your feedback<textarea required rows="6" value={feedback.message} onChange={(event) => update("message", event.target.value)} className={`${inputClass} resize-none leading-6`} placeholder="What worked well? What should we improve?" /></label>
              </div>

              <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl bg-blue-50 p-4 text-sm leading-6 text-slate-600"><input type="checkbox" checked={feedback.permission} onChange={(event) => update("permission", event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" /><span>You may feature this feedback publicly with my name, role, and school.</span></label>

              <button type="submit" className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 hover:bg-blue-700 sm:w-auto">Prepare feedback <FaPaperPlane className="text-xs transition-transform group-hover:translate-x-0.5" /></button>
              <p className="mt-4 text-xs text-slate-400">Submitting opens your email app with the completed feedback.</p>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
};

export default FeedbackForm;
